import { AlertTriangle, Plus, TicketPercent } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';

import { usePointsTiers, useSetPointsTierActive } from '../api/coupons.queries';
import { formatCouponScope, formatCouponValue } from '../lib/coupon-format';
import type { PointsTier } from '../types/coupons.types';
import { PointsTierDialog } from './points-tier-dialog';

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; tier: PointsTier }
  | { kind: 'status'; tier: PointsTier }
  | null;

/**
 * Points coupons of the country: the tiers customers choose from on My points. Read with
 * coupons.read, changed with coupons.manage. Points never expire; a coupon bought with them does.
 */
export function PointsTiersSection({
  countryId,
  countryName,
  canManage,
}: {
  countryId: number;
  countryName: string;
  canManage: boolean;
}) {
  const tiers = usePointsTiers(countryId);
  const setActive = useSetPointsTierActive();
  const [open, setOpen] = useState<Open>(null);
  const currency = tiers.data?.[0]?.currency ?? null;
  const statusTarget = open?.kind === 'status' ? open.tier : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm">Points coupons</CardTitle>
          <CardDescription className="text-xs">
            What customers can buy with their points. Points spent on a coupon are not returned if
            it expires unused.
          </CardDescription>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ kind: 'create' })}>
            <Plus className="size-3.5" aria-hidden />
            Points tier
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {tiers.isLoading ? (
          <Skeleton className="h-24" />
        ) : tiers.error || !tiers.data ? (
          <ErrorState error={tiers.error} onRetry={() => void tiers.refetch()} />
        ) : tiers.data.length === 0 ? (
          <EmptyState
            icon={TicketPercent}
            title="No points tiers"
            description={`Customers in ${countryName} cannot exchange points for a coupon yet.`}
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tiers.data.map((tier) => (
              <li key={tier.id} className="rounded-md border p-3 text-xs">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold tabular-nums">
                      {tier.pointsCost.toLocaleString()} points
                    </p>
                    <p className="tabular-nums">{formatCouponValue(tier, tier.currency)}</p>
                  </div>
                  <StatusBadge variant={tier.isActive ? 'success' : 'neutral'}>
                    {tier.isActive ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </div>
                <p className="text-muted-foreground mt-2">
                  {formatCouponScope(tier)} · valid {tier.validityDays} days · {tier.issuedCount}{' '}
                  bought
                </p>
                {tier.marginWarning && (
                  <p className="text-status-warning mt-2 flex items-start gap-1">
                    <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                    {tier.marginWarning}
                  </p>
                )}
                {canManage && (
                  <div className="mt-2 flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpen({ kind: 'edit', tier })}
                      aria-label={`Edit the ${tier.pointsCost} points tier`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpen({ kind: 'status', tier })}
                      aria-label={`${tier.isActive ? 'Deactivate' : 'Activate'} the ${tier.pointsCost} points tier`}
                    >
                      {tier.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {open?.kind === 'create' && (
        <PointsTierDialog
          countryId={countryId}
          countryName={countryName}
          currency={currency}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'edit' && (
        <PointsTierDialog
          countryId={countryId}
          countryName={countryName}
          currency={open.tier.currency}
          tier={open.tier}
          onClose={() => setOpen(null)}
        />
      )}
      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(isOpen) => !isOpen && setOpen(null)}
        title={statusTarget?.isActive ? 'Deactivate this tier?' : 'Activate this tier?'}
        description={
          statusTarget?.isActive
            ? 'Customers can no longer buy it. Coupons already bought stay valid.'
            : 'Customers can buy it again with their points.'
        }
        confirmLabel={statusTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={statusTarget?.isActive ? 'destructive' : 'default'}
        isPending={setActive.isPending}
        onConfirm={() =>
          statusTarget &&
          setActive.mutate(
            { id: statusTarget.id, active: !statusTarget.isActive },
            { onSuccess: () => setOpen(null) },
          )
        }
      />
    </Card>
  );
}
